import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import * as Promise from 'bluebird';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { IAjax } from 'lib/Ajax';
import { DraftObject } from 'lib/draftSignals';
import { AutoSavingDraftObject } from 'lib/autoSave';
import { ValidationWatcher } from 'lib/validationWatcher';
import nonuniqueIndex, { NonuniqueIndex } from 'lib/nonuniqueIndex';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { Pilot, PilotOptions } from 'data/models/Pilot';
import { PilotSection, PilotSectionOptions } from 'data/models/PilotSection';
import { RotationDashboard as ServerRotationDashboard, RotationDashboardOptions } from 'data/models/RotationDashboard';

import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';

export const enum PageMode {
    View,
    SetRotationOrder,
    ManageSections
}

export interface RotationDashboardParams {
    id : number;
    mode : PageMode;
}

export function RotationDashboard(
    ajax : IAjax,
    loader : CachedModelLoader<ServerRotationDashboard, RotationDashboardOptions>,
    entrypoints : Entrypoints,
    data : PCCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    { id, mode } : RotationDashboardParams
) {
    const { model } = loader({
            Id: id,
            // TH 10/20/2017 -- Seed navbar too, so it can load w/ deault values as soon as possible
            Navbar: { Id: id }
        }, 30),
        pageMode = S.data(mode),
        editingSection = S.data<PilotSection | null>(null),
        editSectionModal = S(() => editingSection() ? EditSectionViewModel(editingSection()!, ajax, data, () => editingSection(null)) : null),
        // Either holds a delete fn that returns a promise or else null
        deletingSection = S.data<(() => Promise<any>) | null>(null),
        deleteSectionModal = S(() => deletingSection() ? DeleteSectionViewModel(deletingSection()!, () => deletingSection(null)) : null),
        pilots = model.Pilots.map(p => PilotViewModel(p, ajax, personProfilePopupModel)),
        pilotsBySection = nonuniqueIndex(pilots, p => p.Model.SectionId() || 0),
        sections = lift(() => model.PilotAgency() ? model.PilotAgency()!.Sections() : [])
            .map(s => PilotSectionViewModel(s, ajax, entrypoints, data, sectionId => PilotGroupingViewModel(sectionId, pilotsBySection), editingSection, deletingSection))
            .sort((p1, p2) => p1.Model.Order() - p2.Model.Order()),
        addSectionClient = ajax.Url<PilotSectionOptions>(() => model.PilotAgency() && model.PilotAgency()!.SectionsUrl() || '');

    return {
        Model: model,
        PageMode: pageMode,
        Sections: sections,
        OnSortSections,
        UnassignedPilots: PilotGroupingViewModel(null, pilotsBySection),
        ShowAddSection: () => !!model.PilotAgency() && pageMode() === PageMode.ManageSections,
        AddSection,
        EditSectionModal: editSectionModal,
        DeleteSectionModal: deleteSectionModal
    };

    function AddSection() {
        const pilotAgency = model.PilotAgency();
        if (!pilotAgency) return;
        const nextSectionOrder = pilotAgency.Sections().length ?
            Math.max(...pilotAgency.Sections().map(s => s.Order())) + 1 : 0,
            req = addSectionClient.Post({
                Id: 0,
                Name: 'New Section',
                Order: nextSectionOrder
            });
        req.then(json => data.PilotSections.update1(req.Date, json));
    }
}
export type RotationDashboard = ReturnType<typeof RotationDashboard>;

function OnSortSections(section : PilotSectionViewModel, sections : PilotSectionViewModel[]) {
    S.freeze(() => sections.forEach(SetItemOrder));
}

function SetPilotSortOrder(
    sectionId : number | null,
    pilot : PilotViewModel,
    targetPilots : PilotViewModel[],
    sourcePilots : PilotViewModel[] | null
) {
    S.freeze(() => {
        pilot.Model.SectionId(sectionId);
        targetPilots.forEach(SetItemOrder);
        sourcePilots && sourcePilots.forEach(SetItemOrder);
    });
}

function SetItemOrder(item : { Model : { Order : DataSignal<number> } }, order : number) {
    item.Model.Order(order);
}

// ViewModel properties that apply to PilotSection entity or the unassigned pilots area
function PilotGroupingViewModel(
    sectionId : number | null,
    pilotsBySection : NonuniqueIndex<PilotViewModel>
) {
    const pilots = pilotsBySection(sectionId || 0)
        .sort((p1, p2) => p1.Model.Order() - p2.Model.Order());

    return {
        Pilots: pilots,
        OnSortPilots: (
            pilot : PilotViewModel,
            targetPilots : PilotViewModel[],
            sourcePilots : PilotViewModel[] | null
        ) => SetPilotSortOrder(sectionId, pilot, targetPilots, sourcePilots)
    };
}
export type PilotGroupingViewModel = ReturnType<typeof PilotGroupingViewModel>;

// ViewModel for an actual PilotSection entity
function PilotSectionViewModel(
    pilotSection : PilotSection,
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    groupingModel : (sectionId : number | null) => PilotGroupingViewModel,
    editingSection : DataSignal<PilotSection | null>,
    deletingSection : DataSignal<(() => Promise<any>) | null>
) {
    const client = ajax.Url<PilotSection>(() => pilotSection.Url() || ''),
        draft = AutoSavingDraftObject(pilotSection, client),
        deleteClient = ajax.Url<{}, PilotOptions[]>(() => entrypoints.DeletePilotSection({ Id: pilotSection.Id() })),
        groupingVM = groupingModel(S.sample(pilotSection.Id));

    return Object.assign(groupingVM, {
        Model: draft,
        ShowEditName,
        ShowDeleteSection
    });

    function ShowEditName() {
        editingSection(pilotSection);
    }

    function ShowDeleteSection() {
        deletingSection(Delete);
    }

    function Delete() {
        const req = deleteClient.Post({}),
            id = pilotSection.Id();
        return req.then(json => S.freeze(() => {
            const ts = req.Date;
            data.PilotSections.update(ts, [], [id]);
            data.Pilots.update(ts, json, []);
        }));
    }
}
export type PilotSectionViewModel = ReturnType<typeof PilotSectionViewModel>;

function PilotViewModel(
    pilot : Pilot,
    ajax : IAjax,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const client = ajax.Url<Pilot>(() => pilot.Url() || ''),
        draft = AutoSavingDraftObject(pilot, client),
        displayName = () => pilot.User()?.AbbreviatedName() ?? '',
        showProfile = S.data(false),
        personProfilePopup = S(() => showProfile() ? personProfilePopupModel({ id: pilot.Id(), onHidePopup: () => showProfile(false) }) : null);

    return {
        Model: draft,
        DisplayName: displayName,
        PersonProfilePopup: personProfilePopup,
        ToggleProfile: () => showProfile(!showProfile())
    };
}
export type PilotViewModel = ReturnType<typeof PilotViewModel>;

function EditSectionViewModel(
    pilotSection : PilotSection,
    ajax : IAjax,
    data : PCCache,
    onHidePopup : () => void
) {
    const { object: draft } = DraftObject(pilotSection),
        client = ajax.Url<PilotSection, PilotSectionOptions>(() => pilotSection.Url() || ''),
        validation = ValidationWatcher(client),
        loading = S.data(false);

    return {
        Model: draft,
        SaveChanges,
        Cancel: onHidePopup,
        Validation: validation,
        Loading: loading
    };

    function SaveChanges() {
        if (loading() || draft.Name() === pilotSection.Name()) return;
        loading(true);
        const req = client.Put(draft);
        req.then(json => S.freeze(() => {
            data.PilotSections.update1(req.Date, json);
            onHidePopup();
        })).finally(() => loading(false));
    }
}
export type EditSectionViewModel = ReturnType<typeof EditSectionViewModel>;

function DeleteSectionViewModel(
    deleteSection : () => Promise<any>,
    onHidePopup : () => void
) {
    const loading = S.data(false);

    return {
        Loading: loading,
        Delete,
        Cancel: onHidePopup
    };

    function Delete() {
        if (loading()) return;
        loading(true);
        deleteSection().then(onHidePopup).finally(() => loading(false));
    }
}
export type DeleteSectionViewModel = ReturnType<typeof DeleteSectionViewModel>;


// WEBPACK FOOTER //
// ./apps/pilots/rotationdashboard/model.ts